import { Flex } from '@zillow/constellation';
import React, { useCallback } from 'react';
import { VoidConfirmationModal } from './VoidConfirmationModal';
import { VoidReason } from './VoidReason';
import { Alert } from '@zillow/constellation';
import { useSelectCurrentOrderId } from 'selectors/order/selectCurrentOrder';
import { assertDefined } from 'utilities/Assertions';
import { IVoidData } from './IVoidData';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { IInternalTransferDisbursementFormData } from 'types/order/escrow/internalTransferDisbursements/IInternalTransferDisbursementFormData';
import { useVoidInternalDisbursementMutation } from 'API/order/escrow/InternalTransferDisbursementAPI';
import { voidInternalTransferSchema } from './schemas';
import { Confirmation } from './Confirmation';
import { VoidInternalTransferDetails } from './VoidInternalTransferDetails';
import { IInternalTransferData } from 'types/order/escrow/IInternalTransferData';
import { eVoidReason, voidReasonOptions } from 'types/accounting/eVoidReason';
import { useHasPermissions } from 'components/accessControl/hooks/useHasPermissions';

interface IVoidInternalTransfersButtonProps {
  internalDisbursement: IInternalTransferDisbursementFormData | IInternalTransferData;
  destinationAccountName?: string;
}

export const VoidInternalTransfersButton: React.FC<IVoidInternalTransfersButtonProps> = ({
  internalDisbursement,
  destinationAccountName,
}) => {
  const orderId = useSelectCurrentOrderId();
  assertDefined(orderId);

  const [onVoidInternalTransfer] = useVoidInternalDisbursementMutation();

  const onVoid = useCallback(
    async (values: IVoidData) => {
      const result = await onVoidInternalTransfer({
        orderId,
        internalTransfer: {
          id: internalDisbursement.id,
          void_reason: values.voidReason as eVoidReason,
          void_reason_description:
            values.voidReason === eVoidReason.Other || values.voidReason === eVoidReason.SentInError
              ? values.voidReasonDescription
              : undefined,
        },
      });
      checkForMutationSuccess(result, 'voiding wire');
    },
    [orderId, internalDisbursement.id, onVoidInternalTransfer]
  );

  const isVoidable = !internalDisbursement.voidedAt && internalDisbursement.enqueuedForDisbursementAt;
  const hasPermission = useHasPermissions([ePermissions.EditEscrowInternalTransfersDisbursed]);
  if (!hasPermission || !isVoidable) {
    return null;
  }

  return (
    <VoidConfirmationModal
      onVoid={onVoid}
      button-data-test-id={`void-internal-transfer-button-${internalDisbursement.id}`}
      whatAreWeVoiding="Internal Transfer"
      initialValues={{
        checkInHand: false,
        confirmation: false,
        voidReason: '',
        voidReasonDescription: '',
      }}
      validationSchema={voidInternalTransferSchema}
      details={
        <VoidInternalTransferDetails
          internalTransfer={internalDisbursement}
          destinationAccountName={destinationAccountName}
        />
      }
      form={
        <Flex display={'flex'} flexDirection={'column'} gap={1}>
          <VoidReason reasonList={voidReasonOptions} />
          <Confirmation />
          <Alert
            appearance="warning"
            body={`Only Void an Internal Transfer if you've confirmed that the payment was unsuccessful in Wells Fargo Payment Manager. Voiding an Internal Transfer WILL NOT reverse a completed payment.`}
          ></Alert>
        </Flex>
      }
    />
  );
};
